import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { AppIcon } from '../components/AppIcon';
import { company } from '../data/company';
import { getAppBySlug } from '../data/apps';
import { getInternalLegalDoc } from '../data/legal';

export function AppLegalPage({ type }) {
  const { appSlug } = useParams();
  const app = getAppBySlug(appSlug);

  if (!app) {
    return <Navigate to="/apps" replace />;
  }

  if (app.legalMode === 'external') {
    return <ExternalLegalRedirect app={app} type={type} />;
  }

  const doc = getInternalLegalDoc(app.slug, type);

  if (!doc) {
    return <Navigate to="/apps" replace />;
  }

  return <InternalLegalPage app={app} doc={doc} />;
}

function ExternalLegalRedirect({ app, type }) {
  const externalUrl = type === 'privacy' ? app.privacyUrl : app.termsUrl;

  React.useEffect(() => {
    if (externalUrl) {
      window.location.replace(externalUrl);
    }
  }, [externalUrl]);

  if (!externalUrl) {
    return <Navigate to="/apps" replace />;
  }

  const title = type === 'privacy' ? 'Privacy Policy' : 'Terms of Service';

  return (
    <section className="section-page">
      <div className="section-heading">
        <p className="eyebrow">{app.name}</p>
        <h1>{title}</h1>
        <p>Opening the existing {app.name} {title.toLowerCase()} page.</p>
        <div className="hero-actions">
          <a className="primary-action" href={externalUrl}>Open page</a>
          <Link className="secondary-action" to={`/apps/${app.slug}`}>Back to app</Link>
        </div>
      </div>
    </section>
  );
}

function InternalLegalPage({ app, doc }) {
  const labels = {
    lastUpdated: doc.labels?.lastUpdated || 'Last updated',
    noteTitle: doc.labels?.noteTitle || 'App-specific page',
    noteBody: doc.labels?.noteBody || `This page applies to ${app.name}. Other ${company.name} apps may have separate policies and terms.`,
    contactBody: doc.labels?.contactBody || 'For privacy, support, or legal questions, email',
  };

  React.useEffect(() => {
    document.title = `${doc.title} | ${app.name} | ${company.name}`;
  }, [app.name, doc.title]);

  return (
    <section className="legal-page" lang={doc.locale || 'en'}>
      <div className="legal-hero legal-app-hero">
        <div>
          <p className="eyebrow">{app.name}</p>
          <h1>{doc.title}</h1>
          <p>{doc.intro}</p>
          {doc.effectiveDate ? <span>Effective date: {doc.effectiveDate}<br /></span> : null}
          <span>{labels.lastUpdated}: {doc.lastUpdated}</span>
        </div>
        <AppIcon app={app} size="large" />
      </div>
      <div className="legal-layout">
        <aside className="legal-note">
          <strong>{labels.noteTitle}</strong>
          <p>{labels.noteBody}</p>
          <p>{labels.contactBody} <a href={`mailto:${company.contactEmail}`}>{company.contactEmail}</a>.</p>
        </aside>
        <div className="legal-content">
          {doc.sections.map((section) => (
            <article className="legal-section" key={section.title}>
              <h2>{section.title}</h2>
              <LegalSectionContent section={section} />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function LegalSectionContent({ section }) {
  if (section.blocks) {
    return section.blocks.map((block, index) => {
      if (block.type === 'list') {
        return (
          <ul className="legal-list" key={`list-${index}`}>
            {block.items.map((item) => <li key={item}>{item}</li>)}
          </ul>
        );
      }

      if (block.type === 'links') {
        return (
          <ul className="legal-links" key={`links-${index}`}>
            {block.links.map((link) => (
              <li key={link.href}>
                <a href={link.href} target="_blank" rel="noreferrer">{link.label}</a>
              </li>
            ))}
          </ul>
        );
      }

      return <p key={`paragraph-${index}`}>{block.text}</p>;
    });
  }

  return (
    <>
      {section.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      {section.items ? (
        <ul className="legal-list">
          {section.items.map((item) => <li key={item}>{item}</li>)}
        </ul>
      ) : null}
      {section.links ? (
        <ul className="legal-links">
          {section.links.map((link) => (
            <li key={link.href}>
              <a href={link.href} target="_blank" rel="noreferrer">{link.label}</a>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}
